<template>
  <div>
    <b-card class="m-3" no-body>
      <template #header>
        <h6 class="mb-0 text-center titulo-sagres font-weight-bold">
          Relatório Folha de Pagamento por CPF
        </h6>
      </template>
      <b-form v-if="!blockForm" class="m-2" @submit.stop.prevent="gerarRelatorio">
        <b-row>
          <b-col cols="12" xl="2">
            <b-form-group id="input-group-exercicio" label="Exercício *" label-for="input-exercicio"
              label-class="text-left">
              <b-form-select id="input-exercicio" v-model="filtros.exercicio" :options="exercicios"
                @change="prepararFppc()"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6" xl="2">
            <b-form-group id="input-group-cpf" label="CPF *" label-for="input-cpf" label-class="text-left">
              <b-form-input id="input-cpf" v-model="filtros.cpf" type="text" maxlength="11"
                :disabled="desabilitarFppc || loading"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" xl="2">
            <b-form-group id="input-group-mesInicio" label="Mês Início *" label-for="input-mesInicio"
              label-class="text-left">
              <b-form-select id="input-mesInicio" v-model="filtros.mesInicio" :options="mesesDisponiveis"
                :disabled="desabilitarFppc || loading"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="12" xl="2">
            <b-form-group id="input-group-mesFim" label="Mês Fim *" label-for="input-mesFim" label-class="text-left">
              <b-form-select id="input-mesFim" v-model="filtros.mesFim" :options="mesesDisponiveis"
                :disabled="desabilitarFppc || loading"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="12" xl="4" class="d-flex align-items-center justify-content-end">
            <b-button class="mr-2" size="md" variant="secondary" type="submit"
              :disabled="!filtrosPreenchidos || desabilitarFppc || loading">
              <font-awesome-icon icon="file-alt" class="mr-2" />
              Gerar Relatório
            </b-button>
          </b-col>
        </b-row>
      </b-form>
      <div v-else class="text-center">
        <b-spinner label="Carregando..."></b-spinner>
      </div>
    </b-card>
    <div v-if="loading" class="text-center">
      <b-spinner label="Carregando..."></b-spinner>
    </div>
    <pdf-viewer v-if="!loading && pdfData" :data="pdfData" :filename="filename" />
  </div>
</template>

<script>
import reportService from '@/services/reportService'
import PdfViewer from "@/components/PdfViewer.vue"
import { UtilsTS } from '@/utilsts'
import { FiltrosGestao } from '@/types/relatorios/filtros-gestao'

export default {
  mixins: [UtilsTS],
  components: {
    PdfViewer
  },
  data() {
    return {
      filename: null,
      pdfData: null,
      desabilitarFppc: false,
      meses: [],
      mesesDisponiveis: [],
      exercicios: [],
      filtros: {
        exercicio: null,
        mesInicio: null,
        mesFim: null,
        cpf: null,
      },
      loading: false,
      blockForm: false,
      tentarAnoAnterior: true,
    }
  },
  methods: {
    async prepararFppc() {
      this.blockForm = true
      try {
        const prepararFppcResponse = await reportService.prepararFppc(this.filtros.exercicio ?? new Date().getFullYear())
        if (prepararFppcResponse && prepararFppcResponse.status == 200) {
          this.desabilitarFppc = prepararFppcResponse.data.desabilitarFPPC
          if (!this.desabilitarFppc) {
            this.filtros.exercicio = prepararFppcResponse.data.exercicioSelecionado
            this.exercicios = prepararFppcResponse.data.exercicios.map(e => {
              return { value: e, text: e }
            })
            const ultimoMes = prepararFppcResponse.data.ultimoMes
            this.mesesDisponiveis = this.meses.filter(m => m.value <= ultimoMes)
            this.filtros.mesInicio = this.mesesDisponiveis[0].value
            this.filtros.mesFim = this.mesesDisponiveis[0].value
          } else {
            /** malandragem criada em 2024/01:
             * Todo começo de ano esse relatório dá pau pq não temos dados do ano ainda, e estes componentes pedem o ano 
             * corrente na inicialização. Então na primeira execução deste componente, se vier a flag pra desabilitar,
             * a gente muda o filtro pro ano anterior e tenta novamente. A flag existe pra garantir que isso só vai 
             * ser tentado 1 vez, e só na inicialização do componente.
             */
            if (this.tentarAnoAnterior) {
              this.tentarAnoAnterior = false
              this.filtros.exercicio = (new Date().getFullYear()) - 1
              return this.prepararFppc();
            }
            this.$toast.error('erro ao preparar relatório')
          }
        } else {
          this.$toast.error('erro ao preparar relatório')
        }
      } catch (e) {
        this.tratarError(e)
      } finally {
        this.tentarAnoAnterior = false
        this.blockForm = false
      }
    },

    async gerarRelatorio() {
      try {
        this.pdfData = null;
        this.loading = true;
        const ftr = new FiltrosGestao()
        ftr.exercicio = this.filtros.exercicio
        ftr.mesInicio = this.filtros.mesInicio
        ftr.mesFim = this.filtros.mesFim
        ftr.cpfFiltro = this.filtros.cpf
        const response = await reportService.getRelatorioGestao('FolhaPagamentoCpf', ftr)
        this.filename = "FolhaPorCPF_" + this.filtros.exercicio + "_" + this.filtros.mesInicio + "_" + this.filtros.mesFim + "_" + this.filtros.cpf;
        const blob = this.base64ToPdf(response.ArquivoPdf)
        const objectUrl = URL.createObjectURL(blob)
        this.pdfData = objectUrl;
      } catch (e) {
        this.tratarError(e)
      } finally {
        this.loading = false;
      }
    }
  },

  computed: {
    filtrosPreenchidos() {
      return this.filtros.exercicio && this.filtros.cpf && this.filtros.mesInicio && this.filtros.mesFim
    }
  },

  async mounted() {
    this.meses = this.getListaMeses()
    await this.prepararFppc()
  }

}
</script>

<style>
#cardFiltros {
  text-align: left;
  margin: 20px auto !important;
  width: 95%;
}
</style>